import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Clock, Play, Square, DollarSign, Calendar, Plus } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const TimeTrackingPage = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ hours_this_week: 0, hours_this_month: 0, earnings_this_week: '$0', earnings_this_month: '$0' });
  const [isTracking, setIsTracking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [selectedProject, setSelectedProject] = useState('TechCorp AI');
  const [description, setDescription] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualDate, setManualDate] = useState(new Date().toISOString().split('T')[0]);
  const intervalRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadEntries(); return () => clearInterval(intervalRef.current); }, [user]);

  const loadEntries = async () => {
    try {
      const data = await api.getTimeEntries();
      setEntries(data.entries || []);
      setStats(data.stats || stats);
    } catch {
      setEntries([
        { entry_id: '1', project: 'TechCorp AI', description: 'Implemented ML pipeline', hours: 8.5, date: '2025-01-29', status: 'approved' },
        { entry_id: '2', project: 'DataMinds', description: 'Code review and optimization', hours: 4, date: '2025-01-28', status: 'pending' },
      ]);
      setStats({ hours_this_week: 14.5, hours_this_month: 145, earnings_this_week: '$1,740', earnings_this_month: '$17,400' });
    } finally { setLoading(false); }
  };

  const startTimer = () => {
    setIsTracking(true);
    setElapsed(0);
    intervalRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
  };

  const stopTimer = async () => {
    clearInterval(intervalRef.current);
    setIsTracking(false);
    const hours = Math.round((elapsed / 3600) * 100) / 100;
    if (hours < 0.01) return;
    const entry = { contract_id: 'active', project: selectedProject, description, hours, date: new Date().toISOString().split('T')[0] };
    try {
      const res = await api.createTimeEntry(entry);
      setEntries(prev => [res, ...prev]);
    } catch {
      setEntries(prev => [{ entry_id: `temp_${Date.now()}`, ...entry, status: 'pending' }, ...prev]);
    }
    toast({ title: 'Time logged!', description: `${hours.toFixed(2)} hours logged for ${selectedProject}` });
    setElapsed(0);
    setDescription('');
  };

  const addManualEntry = async () => {
    if (!manualHours) return;
    const entry = { contract_id: 'active', project: selectedProject, description, hours: parseFloat(manualHours), date: manualDate };
    try {
      const res = await api.createTimeEntry(entry);
      setEntries(prev => [res, ...prev]);
    } catch {
      setEntries(prev => [{ entry_id: `temp_${Date.now()}`, ...entry, status: 'pending' }, ...prev]);
    }
    toast({ title: 'Time entry added!', description: `${manualHours} hours logged for ${selectedProject}` });
    setShowManual(false);
    setManualHours('');
    setDescription('');
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, '0');
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${h}:${m}:${sec}`;
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div><h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Time Tracking</h1><p className="text-gray-600 text-sm sm:text-base">Track your work hours and manage time entries</p></div>
            <Button onClick={() => setShowManual(!showManual)} variant="outline" className="w-full sm:w-auto"><Plus className="w-4 h-4 mr-2" />Manual Entry</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 text-center"><Clock className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.hours_this_week}h</div><div className="text-sm text-gray-600">This Week</div></Card>
            <Card className="p-6 text-center"><Calendar className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.hours_this_month}h</div><div className="text-sm text-gray-600">This Month</div></Card>
            <Card className="p-6 text-center"><DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.earnings_this_week}</div><div className="text-sm text-gray-600">Week Earnings</div></Card>
            <Card className="p-6 text-center"><DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.earnings_this_month}</div><div className="text-sm text-gray-600">Month Earnings</div></Card>
          </div>

          {/* Timer */}
          <Card className="p-8 mb-8 text-center">
            <div className="text-6xl font-mono font-bold text-gray-900 mb-6">{formatTime(elapsed)}</div>
            <div className="flex items-center justify-center gap-4 mb-4">
              <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-md">
                <option>TechCorp AI</option><option>DataMinds</option><option>AI Research Lab</option>
              </select>
              <Input className="max-w-xs" placeholder="What are you working on?" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            {isTracking ? (
              <Button onClick={stopTimer} className="bg-red-600 hover:bg-red-700 px-8 py-3"><Square className="w-5 h-5 mr-2" />Stop Timer</Button>
            ) : (
              <Button onClick={startTimer} className="bg-[#0A3900] hover:bg-[#1A5C0A] px-8 py-3"><Play className="w-5 h-5 mr-2" />Start Timer</Button>
            )}
          </Card>

          {/* Manual Entry Form */}
          {showManual && (
            <Card className="p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Manual Entry</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
                  <option>TechCorp AI</option><option>DataMinds</option><option>AI Research Lab</option>
                </select>
                <Input placeholder="Hours (e.g. 4.5)" value={manualHours} onChange={(e) => setManualHours(e.target.value)} type="number" step="0.25" />
                <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
                <Button onClick={addManualEntry} className="bg-[#0A3900] hover:bg-[#1A5C0A]">Add Entry</Button>
              </div>
            </Card>
          )}

          {/* Entries Table */}
          <Card>
            <div className="p-6 border-b"><h2 className="text-xl font-semibold text-gray-900">Recent Time Entries</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr></thead>
                <tbody className="divide-y">
                  {entries.map((e) => (
                    <tr key={e.entry_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{e.date}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{e.project}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{e.description}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{e.hours}h</td>
                      <td className="px-6 py-4"><Badge className={e.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{e.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingPage;
